// Attribute-parsing helpers for reading saved GrapesJS HTML. Moved here from
// backend/guest-portal/backend/rental-agreements/attributeParsing.ts (which
// re-exports them) so the guest portal and dashboard can use the same
// implementations. Keep this module free of server-only imports; it runs in
// the browser.
import { findElementStart } from '../rentalAgreements/multiSelect.js';
/**
 * Decode HTML entities in attribute values. GrapesJS HTML-encodes attribute
 * values when it serializes the editor canvas (e.g. `"` -> `&quot;`,
 * `'` -> `&#039;`, `&` -> `&amp;`), so values read back out of the saved HTML
 * must be decoded before use.
 */
export const decodeHtmlEntities = (str) => str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&');
/** Escape text for safe interpolation into HTML markup (element or attribute context). */
export const escapeHtml = (str) => String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
/**
 * BES-7582: Read a single attribute's raw (still entity-encoded) value from an
 * element's opening tag. Matches double- OR single-quoted values and allows the
 * OPPOSITE quote char inside. GrapesJS double-quotes attributes and escapes
 * interior double quotes to &quot;, but leaves apostrophes literal, so a value
 * like `data-guest-fields="[...&quot;Driver's license&quot;...]"` must not be
 * truncated at the apostrophe. The old /attr=(["'])([^"']*)\1/ pattern excluded
 * BOTH quotes, so an apostrophe broke the match and the field list silently
 * dropped to []. Returns null when the attribute is absent.
 */
export const getRawAttr = (element, attr) => {
    const match = element.match(new RegExp(`${attr}=(?:"([^"]*)"|'([^']*)')`));
    return match ? (match[1] ?? match[2]) : null;
};
/**
 * Given the index of an attribute inside an element's opening tag, return the
 * full opening tag string (`<...>`). Finds the opening `<` quote-aware
 * (findElementStart) and scans forward to the terminating `>`, skipping any `<`
 * or `>` inside a quoted attribute value. Returns null if the tag cannot be bounded.
 */
export const extractOpeningTag = (html, attrIndex) => {
    const start = findElementStart(html, attrIndex);
    if (start === -1)
        return null;
    let quote = null;
    for (let i = start + 1; i < html.length; i++) {
        const ch = html[i];
        if (quote) {
            if (ch === quote)
                quote = null;
        }
        else if (ch === '"' || ch === "'") {
            quote = ch;
        }
        else if (ch === '>') {
            return html.slice(start, i + 1);
        }
    }
    return null;
};
